/**
 * Single source of truth for Lead Status and Property Type constants.
 * Import from here — do NOT redefine these in individual components.
 */

export const LEAD_STATUSES = [
  { id: 1,  name: 'AssignedToEmployee' },
  { id: 2,  name: 'HotClient' },
  { id: 3,  name: 'Interested' },
  { id: 4,  name: 'NotInterested' },
  { id: 5,  name: 'PhoneNotPicked' },
  { id: 6,  name: 'InvalidNumber' },
  { id: 8,  name: 'CallBackLater' },
  { id: 9,  name: 'UpcomingVisit' },
  { id: 10, name: 'Complete' },
  { id: 11, name: 'Visited' },
  { id: 12, name: 'DealDone' },
  { id: 13, name: 'PaymentPending' },
  { id: 14, name: 'Closed' },
  { id: 15, name: 'NewImported' },
];

/** Human-readable labels for pipeline stages and status badges. */
export const STATUS_DISPLAY_NAMES = {
  AssignedToEmployee: 'Assigned',
  HotClient: 'Hot Client',
  Interested: 'Interested',
  NotInterested: 'Not Interested',
  PhoneNotPicked: 'Phone Not Picked',
  InvalidNumber: 'Invalid Number',
  CallBackLater: 'Callback Later',
  UpcomingVisit: 'Upcoming Visit',
  Complete: 'Complete',
  Visited: 'Visited',
  DealDone: 'Deal Done',
  PaymentPending: 'Payment Pending',
  Closed: 'Closed',
  NewImported: 'New / Imported',
  ContactAfterSomeTime: 'Callback Later',
};

export const getStatusDisplayName = (name) =>
  STATUS_DISPLAY_NAMES[name] ?? name ?? 'Unknown';

export const PROPERTY_TYPES = [
  { id: 1, name: '1BHK' },
  { id: 2, name: '2BHK' },
  { id: 3, name: '3BHK' },
  { id: 4, name: '4BHK' },
  { id: 5, name: '5BHK' },
  { id: 6, name: 'Plot' },
  { id: 7, name: 'Kothi' },
  { id: 8, name: 'Other' },
];

/** Numeric-ID → hex color. Used for timeline dots, badges, etc. */
export const STATUS_COLORS = {
  1:  '#8392ab',
  2:  '#f5365c',
  3:  '#2dce89',
  4:  '#f5222d',
  5:  '#faad14',
  6:  '#ff7a45',
  7:  '#1890ff',
  8:  '#722ed1',
  9:  '#344767',
  10: '#13c2c2',
  11: '#5e72e4',
  12: '#00875a',
  13: '#e65100',
  14: '#374151',
  15: '#64748b',
};

/** Status-name → badge style `{bg, color, dot}`. Used for table row badges. */
export const STATUS_STYLE_MAP = {
  AssignedToEmployee:   { bg: '#f4f6fb', color: '#8392ab', dot: '#8392ab' },
  HotClient:            { bg: '#fff0f0', color: '#f5365c', dot: '#f5365c' },
  Interested:           { bg: '#f0fff8', color: '#2dce89', dot: '#2dce89' },
  NotInterested:        { bg: '#f4f6fb', color: '#8392ab', dot: '#8392ab' },
  PhoneNotPicked:       { bg: '#fffbf0', color: '#faad14', dot: '#faad14' },
  InvalidNumber:        { bg: '#fff0f0', color: '#f5365c', dot: '#f5365c' },
  ContactAfterSomeTime: { bg: '#f9f0ff', color: '#722ed1', dot: '#722ed1' },
  CallBackLater:        { bg: '#f9f0ff', color: '#722ed1', dot: '#722ed1' },
  UpcomingVisit:        { bg: '#fff7f0', color: '#fb6340', dot: '#fb6340' },
  Complete:             { bg: '#f0fff8', color: '#2dce89', dot: '#2dce89' },
  Visited:              { bg: '#f0faff', color: '#11cdef', dot: '#11cdef' },
  DealDone:             { bg: '#f0fff4', color: '#00875a', dot: '#00875a' },
  PaymentPending:       { bg: '#fffde7', color: '#e65100', dot: '#e65100' },
  Closed:               { bg: '#f3f4f6', color: '#374151', dot: '#374151' },
  NewImported:          { bg: '#f1f5f9', color: '#64748b', dot: '#64748b' },
};

const DEFAULT_STYLE = { bg: '#f4f6fb', color: '#8392ab', dot: '#8392ab' };

/** Return the display name for a status ID (falls back to 'Unknown'). */
export const getStatusName = (id) =>
  LEAD_STATUSES.find((s) => s.id === Number(id))?.name ?? 'Unknown';

/** Return the hex color for a status ID (falls back to grey). */
export const getStatusColor = (id) => STATUS_COLORS[Number(id)] ?? '#8392ab';

/** Return the badge style object `{bg, color, dot}` for a status name. */
export const getStatusStyle = (name) => STATUS_STYLE_MAP[name] ?? DEFAULT_STYLE;

/** Return the display name for a property type ID (falls back to 'Unknown'). */
export const getPropertyTypeName = (id) =>
  PROPERTY_TYPES.find((p) => p.id === Number(id))?.name ?? 'Unknown';
